import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const STATUSES = [
  { value: "none", label: "Not started" },
  { value: "needed", label: "Needed — plan to apply" },
  { value: "applied", label: "Applied — waiting" },
  { value: "obtained", label: "Obtained ✓" },
  { value: "not_required", label: "Not required in my state" },
];

export default function WorkPermit() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(user?.state || "CA");
  const [info, setInfo] = useState(null);
  const [permitStatus, setPermitStatus] = useState(user?.work_permit_status || "none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state) api.get(`/work-permit/${state}`).then((r)=>setInfo(r.data));
  }, [state]);

  const savePermit = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { state, work_permit_status: permitStatus });
      await refresh();
      toast.success("Permit status saved");
    } catch(e){ toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="nh-container py-14">
      <Toaster position="top-center"/>
      <span className="nh-tag bg-nh-sky"><ShieldCheck size={12} className="mr-1"/>Youth Work Permit Assistant</span>
      <h1 className="font-display font-black text-4xl sm:text-5xl mt-3">Work Permits, Explained.</h1>
      <p className="text-nh-muted mt-2 max-w-2xl">In many states, minors need a work permit or age certificate before their first day of work. We'll tell you what your state requires.</p>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="nh-card p-6">
          <h2 className="font-display font-black text-2xl">Your state</h2>
          <label className="text-xs font-bold uppercase mt-3 block">Select state</label>
          <select className="nh-input mt-1" value={state} onChange={(e)=>setState(e.target.value)} data-testid="wp-state">
            {STATES.map(s=><option key={s} value={s}>{s}</option>)}
          </select>

          {info && info.known && (
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-xs uppercase font-bold text-nh-muted">Permit required for under-18?</div>
                <div className="mt-1 nh-tag" style={{ background: info.required ? "#FFD500" : "#B6F3D6" }}>
                  {info.required ? "YES — you'll likely need one" : "NO — federal rules still apply"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-nh-muted">What to do</div>
                <p className="text-sm mt-1">{info.note}</p>
              </div>
              <a href={info.url} target="_blank" rel="noreferrer" className="nh-btn nh-btn-primary text-xs inline-flex" data-testid="wp-visit-dol">
                <ExternalLink size={14}/> Visit your state's labor page
              </a>
            </div>
          )}
          {info && !info.known && <p className="text-sm mt-4 text-nh-muted">{info.message}</p>}
        </div>

        <div className="nh-card p-6">
          <h2 className="font-display font-black text-2xl">Track my permit</h2>
          <p className="text-sm text-nh-muted mt-1">Save your progress here — it'll show on your applications and unlock jobs faster.</p>
          <label className="text-xs font-bold uppercase mt-4 block">Current status</label>
          <select className="nh-input mt-1" value={permitStatus} onChange={(e)=>setPermitStatus(e.target.value)} data-testid="wp-status">
            {STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={savePermit} disabled={saving} className="nh-btn nh-btn-primary mt-4 text-xs" data-testid="wp-save">
            <CheckCircle2 size={14}/> {saving ? "Saving…" : "Save to profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
